/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionClientsCreate } from "../funcs/remoteSessionClientsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateRemoteSessionClientRequest,
  CreateRemoteSessionClientSecurity,
} from "../models/operations/createremotesessionclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateRemoteSessionClientMutationVariables = {
  request: CreateRemoteSessionClientRequest;
  security?: CreateRemoteSessionClientSecurity | undefined;
  options?: RequestOptions;
};

export type CreateRemoteSessionClientMutationData = RemoteSessionClient;

export type CreateRemoteSessionClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createRemoteSessionClient remoteSessionClients
 *
 * @remarks
 * Register a remote_session_client by supplying a client_id and optional client_secret obtained out-of-band from the upstream issuer.
 */
export function useCreateRemoteSessionClientMutation(
  options?: MutationHookOptions<
    CreateRemoteSessionClientMutationData,
    CreateRemoteSessionClientMutationError,
    CreateRemoteSessionClientMutationVariables
  >,
): UseMutationResult<
  CreateRemoteSessionClientMutationData,
  CreateRemoteSessionClientMutationError,
  CreateRemoteSessionClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateRemoteSessionClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateRemoteSessionClient(): MutationKey {
  return ["@gram/client", "remoteSessionClients", "create"];
}

export function buildCreateRemoteSessionClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateRemoteSessionClientMutationVariables,
  ) => Promise<CreateRemoteSessionClientMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateRemoteSessionClient(),
    mutationFn: function createRemoteSessionClientMutationFn({
      request,
      security,
      options,
    }): Promise<CreateRemoteSessionClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionClientsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
